import type { HabitEntity, Todo, TodoEntity, TodoNavigationCalendarItem, TodoTagEntity } from '../../types/data';
import { getLocalDateString, getTodoBadgeStatusFromTodos } from '../../data/todoData';
import { toCamelCase, toSnakeCase } from '../dataTransform';
import { addTokyoDays, startOfTokyoDate } from '../dateUtils';
import { todoLocalDb } from './todoLocalDb';
import { acceptsServerRow } from './serverRowOrder';
import type { SyncResource, SyncRow } from '../repos/todoSync';
import type { HabitTagMapLocalEntity, TodoStockItemLocalEntity, TodoTagMapLocalEntity } from './todoLocalDb';
import { getLatestSyncCursor, parseSyncCursor, serializeSyncCursor } from '../repos/syncCursor';
import type { SyncCursor } from '../repos/syncCursor';

export const TODO_SYNC_STATE_KEYS = {
  todosLastSyncedAt: 'todos_last_synced_at',
  todoTagMapLastSyncedAt: 'todo_tag_map_last_synced_at',
  todoStockItemsLastSyncedAt: 'todo_stock_items_last_synced_at',
  todoTagsLastSyncedAt: 'todo_tags_last_synced_at',
  habitsLastSyncedAt: 'habits_last_synced_at',
  habitTagMapLastSyncedAt: 'habit_tag_map_last_synced_at',
} as const;

export const todoLocalRepo = {
  async replaceTodos(todos: Todo[]): Promise<void> {
    // Initial fetches can finish after a save; merge instead of deleting newer rows.
    await this.upsertTodos(todos);
  },

  async upsertTodos(todos: Todo[]): Promise<void> {
    await todoLocalDb.transaction('rw', todoLocalDb.todos, todoLocalDb.todo_tag_map, todoLocalDb.todo_stock_items, async () => {
      for (const todo of todos) {
        const entity = toSnakeCase(todo);
        const existing = await todoLocalDb.todos.get(todo.id);
        if (acceptsServerRow(entity, existing)) await todoLocalDb.todos.put(entity);
        // Relations have their own timestamps/cursors. Todo snapshots only seed missing rows.
        for (const row of toTodoTagMapRows(todo)) {
          if (!await todoLocalDb.todo_tag_map.get([row.todo_id, row.tag_id])) await todoLocalDb.todo_tag_map.put(row);
        }
        for (const row of toTodoStockItemRows(todo)) {
          if (!await todoLocalDb.todo_stock_items.where('[todo_id+stock_id]').equals([row.todo_id, row.stock_id]).count()) {
            await todoLocalDb.todo_stock_items.put(row);
          }
        }
      }
    });
  },

  async applyServerRow(resource: SyncResource, row: SyncRow): Promise<void> {
    if (resource === 'todos') {
      const entity = row as unknown as TodoEntity;
      await todoLocalDb.transaction('rw', todoLocalDb.todos, async () => {
        if (acceptsServerRow(entity, await todoLocalDb.todos.get(entity.id))) await todoLocalDb.todos.put(entity);
      });
    } else if (resource === 'todo_tag_map') {
      await this.upsertTodoTagMapRows([row as unknown as TodoTagMapLocalEntity]);
    } else {
      await this.upsertTodoStockItemRows([row as unknown as TodoStockItemLocalEntity]);
    }
  },

  async listTodos(): Promise<Todo[]> {
    const rows = await todoLocalDb.todos
      .orderBy('created_at')
      .toArray();

    return hydrateTodoEntities(rows.filter((row: TodoEntity) => !row.is_deleted));
  },

  async listTodosByDateRange(fromIso: string, toIso: string): Promise<Todo[]> {
    const rows = await todoLocalDb.todos
      .where('start_at')
      .between(fromIso, toIso, true, true)
      .toArray();

    return hydrateTodoEntities(sortTodoEntitiesForDisplay(rows).filter((row: TodoEntity) => !row.is_deleted));
  },

  async listTodosByDate(date: string): Promise<Todo[]> {
    const rows = await todoLocalDb.todos.toArray();

    return hydrateTodoEntities(
      sortTodoEntitiesForDisplay(rows)
        .filter((row: TodoEntity) => !row.is_deleted && getLocalDateString(row.start_at) === date)
    );
  },

  async listTodosByHabit(habitId: string): Promise<Todo[]> {
    const rows = await todoLocalDb.todos
      .where('habit_id')
      .equals(habitId)
      .toArray();

    return hydrateTodoEntities(sortTodoEntitiesForDisplay(rows).filter((row: TodoEntity) => !row.is_deleted));
  },

  async listTodosByTag(tagId: string): Promise<Todo[]> {
    const mapRows = await todoLocalDb.todo_tag_map
      .where('tag_id')
      .equals(tagId)
      .toArray();
    const todoIds = mapRows
      .filter(row => !row.is_deleted)
      .map(row => row.todo_id);
    const rows = await todoLocalDb.todos.bulkGet(todoIds);
    const todoRows = rows.filter((row): row is TodoEntity => !!row);

    return hydrateTodoEntities(sortTodoEntitiesForDisplay(todoRows).filter((row: TodoEntity) => !row.is_deleted));
  },

  async listUncategorizedTodos(): Promise<Todo[]> {
    const [todoRows, mapRows] = await Promise.all([
      todoLocalDb.todos.toArray(),
      todoLocalDb.todo_tag_map.toArray(),
    ]);
    const categorizedTodoIds = new Set(
      mapRows
        .filter(row => !row.is_deleted)
        .map(row => row.todo_id)
    );

    return hydrateTodoEntities(
      sortTodoEntitiesForDisplay(todoRows)
        .filter((row: TodoEntity) => !row.is_deleted && !categorizedTodoIds.has(row.id))
    );
  },

  async listExpiredTodos(nowIso: string): Promise<Todo[]> {
    const rows = await todoLocalDb.todos
      .where('start_at')
      .below(nowIso)
      .toArray();

    return hydrateTodoEntities(
      sortTodoEntitiesForDisplay(rows)
        .filter((row: TodoEntity) => !row.is_deleted && !row.is_done && !row.is_canceled)
    );
  },

  async listCalendarBadgeItems(fromDate: string, toDate: string): Promise<TodoNavigationCalendarItem[]> {
    const fromBoundaryDate = addTokyoDays(fromDate, -1);
    const toBoundaryDate = addTokyoDays(toDate, 2);
    const fromBoundary = fromBoundaryDate ? startOfTokyoDate(fromBoundaryDate) : undefined;
    const toBoundary = toBoundaryDate ? startOfTokyoDate(toBoundaryDate) : undefined;
    if (!fromBoundary || !toBoundary) return [];

    const rows = await todoLocalDb.todos
      .where('start_at')
      .between(fromBoundary.toISOString(), toBoundary.toISOString(), true, false)
      .toArray();
    const todos = rows
      .filter((row: TodoEntity) => !row.is_deleted)
      .map(row => toCamelCase(row) as Todo);

    return getDateStringsInRange(fromDate, toDate)
      .map(date => {
        const badgeStatus = getTodoBadgeStatusFromTodos(date, todos);
        return {
          date,
          has_todo: badgeStatus.hasTodo,
          has_expired_todo: badgeStatus.hasExpiredTodo,
        };
      })
      .filter(item => item.has_todo);
  },

  async getTodoById(id: string): Promise<Todo | undefined> {
    const row = await todoLocalDb.todos.get(id);
    if (!row || row.is_deleted) return undefined;
    const todos = await hydrateTodoEntities([row]);
    return todos[0];
  },

  async clearTodos(): Promise<void> {
    await todoLocalDb.todos.clear();
  },

  async clearAll(): Promise<void> {
    await todoLocalDb.transaction(
      'rw',
      [
        todoLocalDb.todos,
        todoLocalDb.todo_tag_map,
        todoLocalDb.todo_stock_items,
        todoLocalDb.todo_tags,
        todoLocalDb.habits,
        todoLocalDb.habit_tag_map,
        todoLocalDb.sync_state,
      ],
      async () => {
        await Promise.all([
          todoLocalDb.todos.clear(),
          todoLocalDb.todo_tag_map.clear(),
          todoLocalDb.todo_stock_items.clear(),
          todoLocalDb.todo_tags.clear(),
          todoLocalDb.habits.clear(),
          todoLocalDb.habit_tag_map.clear(),
          todoLocalDb.sync_state.clear(),
        ]);
      }
    );
  },

  async getSyncState(key: string): Promise<string | undefined> {
    const row = await todoLocalDb.sync_state.get(key);
    return row?.value;
  },

  async getSyncCursor(key: string): Promise<SyncCursor | undefined> {
    const value = await this.getSyncState(key);
    return parseSyncCursor(value);
  },

  async setSyncState(key: string, value: string): Promise<void> {
    await todoLocalDb.sync_state.put({
      key,
      value,
      updated_at: new Date().toISOString(),
    });
  },

  async setSyncStateFromModifiedAts(key: string, modifiedAts: Array<string | undefined | null>): Promise<void> {
    const latest = getLatestIsoString(modifiedAts);
    if (!latest) return;
    await this.setSyncState(key, latest);
  },

  async setTodoSyncCursorFromRows(todos: Todo[]): Promise<void> {
    const cursor = getLatestSyncCursor(todos.map(todo => ({
      modifiedAt: todo.modifiedAt,
      tieBreaker: [todo.id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.todosLastSyncedAt, serializeSyncCursor(cursor));
  },

  async setTodoTagMapSyncCursorFromRows(rows: TodoTagMapLocalEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(rows.map(row => ({
      modifiedAt: row.modified_at,
      tieBreaker: [row.todo_id, row.tag_id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.todoTagMapLastSyncedAt, serializeSyncCursor(cursor));
  },

  async setTodoStockItemsSyncCursorFromRows(rows: TodoStockItemLocalEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(rows.map(row => ({
      modifiedAt: row.modified_at,
      tieBreaker: [row.id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.todoStockItemsLastSyncedAt, serializeSyncCursor(cursor));
  },

  async setTodoTagsSyncCursorFromRows(tags: TodoTagEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(tags.map(tag => ({
      modifiedAt: tag.modified_at,
      tieBreaker: [tag.id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.todoTagsLastSyncedAt, serializeSyncCursor(cursor));
  },

  async setHabitsSyncCursorFromRows(habits: HabitEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(habits.map(habit => ({
      modifiedAt: habit.modified_at,
      tieBreaker: [habit.id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.habitsLastSyncedAt, serializeSyncCursor(cursor));
  },

  async setHabitTagMapSyncCursorFromRows(rows: HabitTagMapLocalEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(rows.map(row => ({
      modifiedAt: row.modified_at,
      tieBreaker: [row.habit_id, row.tag_id],
    })));
    if (!cursor) return;
    await this.setSyncState(TODO_SYNC_STATE_KEYS.habitTagMapLastSyncedAt, serializeSyncCursor(cursor));
  },

  async deleteSyncState(key: string): Promise<void> {
    await todoLocalDb.sync_state.delete(key);
  },

  async upsertTodoTagMapRows(rows: TodoTagMapLocalEntity[]): Promise<void> {
    await todoLocalDb.transaction('rw', todoLocalDb.todo_tag_map, async () => {
      for (const row of rows) {
        const existing = await todoLocalDb.todo_tag_map.get([row.todo_id, row.tag_id]);
        if (acceptsServerRow(row, existing)) await todoLocalDb.todo_tag_map.put(row);
      }
    });
  },

  async upsertTodoStockItemRows(rows: TodoStockItemLocalEntity[]): Promise<void> {
    await todoLocalDb.transaction('rw', todoLocalDb.todo_stock_items, async () => {
      for (const row of rows) {
        const existing = await todoLocalDb.todo_stock_items.where('[todo_id+stock_id]').equals([row.todo_id, row.stock_id]).toArray();
        if (existing.some(item => !item.id.includes(':') && !acceptsServerRow(row, item))) continue;
        await todoLocalDb.todo_stock_items.bulkDelete(existing.filter(item => item.id !== row.id).map(item => item.id));
        await todoLocalDb.todo_stock_items.put(row);
      }
    });
  },

  async upsertHabitTagMapRows(rows: HabitTagMapLocalEntity[]): Promise<void> {
    await todoLocalDb.habit_tag_map.bulkPut(rows);
  },

  async applyHabitTagMapSync(rows: HabitTagMapLocalEntity[]): Promise<void> {
    const cursor = getLatestSyncCursor(rows.map(row => ({
      modifiedAt: row.modified_at,
      tieBreaker: [row.habit_id, row.tag_id],
    })));
    await todoLocalDb.transaction('rw', todoLocalDb.habit_tag_map, todoLocalDb.sync_state, async () => {
      await todoLocalDb.habit_tag_map.bulkPut(rows);
      if (cursor) {
        await todoLocalDb.sync_state.put({
          key: TODO_SYNC_STATE_KEYS.habitTagMapLastSyncedAt,
          value: serializeSyncCursor(cursor),
          updated_at: new Date().toISOString(),
        });
      }
    });
  },

  async listHabitTagMapRows(): Promise<HabitTagMapLocalEntity[]> {
    return await todoLocalDb.habit_tag_map.toArray();
  },

  async replaceTags(tags: TodoTagEntity[]): Promise<void> {
    await todoLocalDb.transaction('rw', todoLocalDb.todo_tags, async () => {
      await todoLocalDb.todo_tags.clear();
      await todoLocalDb.todo_tags.bulkPut(tags);
    });
  },

  async upsertTags(tags: TodoTagEntity[]): Promise<void> {
    await todoLocalDb.todo_tags.bulkPut(tags);
  },

  async listTags(): Promise<TodoTagEntity[]> {
    const rows = await todoLocalDb.todo_tags.toArray();

    return rows
      .filter((row: TodoTagEntity) => !row.is_deleted)
      .sort((a, b) => {
        const orderCompare = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (orderCompare !== 0) return orderCompare;
        return a.created_at.localeCompare(b.created_at);
      });
  },

  async replaceHabits(habits: HabitEntity[]): Promise<void> {
    await todoLocalDb.transaction('rw', todoLocalDb.habits, async () => {
      await todoLocalDb.habits.clear();
      await todoLocalDb.habits.bulkPut(habits);
    });
  },

  async upsertHabits(habits: HabitEntity[]): Promise<void> {
    await todoLocalDb.habits.bulkPut(habits);
  },

  async listHabits(): Promise<HabitEntity[]> {
    const rows = await todoLocalDb.habits.toArray();

    return rows
      .filter((row: HabitEntity) => !row.is_deleted)
      .sort((a, b) => {
        const orderCompare = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (orderCompare !== 0) return orderCompare;
        return a.created_at.localeCompare(b.created_at);
      });
  },
};

const getLatestIsoString = (values: Array<string | undefined | null>): string | undefined => {
  const sortedValues = values
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .sort((a, b) => a.localeCompare(b));
  return sortedValues[sortedValues.length - 1];
};

const getDateStringsInRange = (fromDate: string, toDate: string): string[] => {
  if (!startOfTokyoDate(fromDate) || !startOfTokyoDate(toDate) || fromDate > toDate) return [];

  const dates: string[] = [];
  let cursor = fromDate;
  while (cursor <= toDate) {
    dates.push(cursor);
    const next = addTokyoDays(cursor, 1);
    if (!next) break;
    cursor = next;
  }
  return dates;
};

const hydrateTodoEntities = async (rows: TodoEntity[]): Promise<Todo[]> => {
  if (rows.length === 0) return [];

  const todoIds = rows.map(row => row.id);
  const [tagMapRows, stockItemRows] = await Promise.all([
    todoLocalDb.todo_tag_map.where('todo_id').anyOf(todoIds).toArray(),
    todoLocalDb.todo_stock_items.where('todo_id').anyOf(todoIds).toArray(),
  ]);
  const tagsByTodo = new Map<string, string[]>();
  tagMapRows
    .filter(row => !row.is_deleted)
    .forEach(row => {
      const tagIds = tagsByTodo.get(row.todo_id) ?? [];
      tagIds.push(row.tag_id);
      tagsByTodo.set(row.todo_id, tagIds);
    });

  const stockItemsByTodo = new Map<string, Todo['stockItems']>();
  stockItemRows
    .filter(row => !row.is_deleted)
    .forEach(row => {
      const stockItems = stockItemsByTodo.get(row.todo_id) ?? [];
      stockItems.push({
        stock_id: row.stock_id,
        is_checked: row.is_checked,
        to_buy: row.to_buy ?? undefined,
      });
      stockItemsByTodo.set(row.todo_id, stockItems);
    });

  return rows.map(row => ({
    ...toCamelCase(row),
    todoTagIds: tagsByTodo.get(row.id) ?? [],
    stockItems: stockItemsByTodo.get(row.id) ?? [],
  }));
};

const toTodoTagMapRows = (todo: Todo): TodoTagMapLocalEntity[] => {
  const nowIso = todo.modifiedAt || new Date().toISOString();
  return (todo.todoTagIds ?? []).map(tagId => ({
    is_snapshot: true,
    todo_id: todo.id,
    tag_id: tagId,
    created_at: todo.createdAt,
    modified_at: nowIso,
    is_deleted: !!todo.deleted,
  }));
};

const toTodoStockItemRows = (todo: Todo): TodoStockItemLocalEntity[] => {
  const nowIso = todo.modifiedAt || new Date().toISOString();
  return (todo.stockItems ?? []).map(item => ({
    id: `${todo.id}:${item.stock_id}`,
    todo_id: todo.id,
    stock_id: item.stock_id,
    is_checked: !!item.is_checked,
    to_buy: item.to_buy ?? null,
    created_at: todo.createdAt,
    modified_at: nowIso,
    is_deleted: !!todo.deleted,
  }));
};

const sortTodoEntitiesForDisplay = (rows: TodoEntity[]): TodoEntity[] => {
  return [...rows].sort((a, b) => {
    const startCompare = a.start_at.localeCompare(b.start_at);
    if (startCompare !== 0) return startCompare;
    return a.created_at.localeCompare(b.created_at);
  });
};
